import type { ColDef } from "ag-grid-community"
import type { ExcelColumn } from "../utils/excelExport"

export type AuditFields = {
  createdAt?: string
  updatedAt?: string
}

const CREATED_AT_KEYS = ["createdAt", "createdDate", "created_at"] as const
const UPDATED_AT_KEYS = ["updatedAt", "updatedDate", "updated_at"] as const

const AUDIT_COLUMN_FIELDS = ["createdAt", "updatedAt"] as const

function pickString(
  source: Record<string, unknown>,
  keys: readonly string[],
): string | null {
  for (const key of keys) {
    const value = source[key]
    if (value != null && String(value).trim()) {
      return String(value).trim()
    }
  }
  return null
}

export function mapAuditFields(item: Record<string, unknown>): AuditFields {
  const createdAt = pickString(item, CREATED_AT_KEYS)
  const updatedAt = pickString(item, UPDATED_AT_KEYS)
  return {
    ...(createdAt ? { createdAt } : {}),
    ...(updatedAt ? { updatedAt } : {}),
  }
}

function resolveAuditSources(data: unknown): Record<string, unknown>[] {
  if (!data || typeof data !== "object") return []

  const record = data as Record<string, unknown>
  const sources = [record]

  if (record.raw && typeof record.raw === "object") {
    sources.push(record.raw as Record<string, unknown>)
  }

  return sources
}

function resolveAuditValue(
  data: unknown,
  keys: readonly string[],
): string | null {
  for (const source of resolveAuditSources(data)) {
    const value = pickString(source, keys)
    if (value) return value
  }
  return null
}

export function formatAuditDateTime(value?: string | null): string {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export const AUDIT_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: "Created At", key: "createdAt" },
  { header: "Updated At", key: "updatedAt" },
]

export function mapAuditExportFields(item: unknown) {
  const fields = mapAuditFields(
    item && typeof item === "object" ? (item as Record<string, unknown>) : {},
  )
  return {
    createdAt: formatAuditDateTime(fields.createdAt),
    updatedAt: formatAuditDateTime(fields.updatedAt),
  }
}

function findActionColumnIndex<T>(columnDefs: ColDef<T>[]): number {
  return columnDefs.findIndex(
    (col) =>
      col.pinned === "right" &&
      (col.headerName === "Action" ||
        col.headerName === "Actions" ||
        col.colId === "actions"),
  )
}

export function createAuditColumnDefs<T>(): ColDef<T>[] {
  return [
    {
      colId: "createdAt",
      headerName: "Created At",
      width: 180,
      cellClass: "text-muted-foreground",
      sortable: true,
      valueGetter: (params) =>
        resolveAuditValue(params.data, CREATED_AT_KEYS),
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? "")),
    },
    {
      colId: "updatedAt",
      headerName: "Updated At",
      width: 180,
      cellClass: "text-muted-foreground",
      sortable: true,
      valueGetter: (params) =>
        resolveAuditValue(params.data, UPDATED_AT_KEYS),
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? "")),
    },
  ]
}

export function withAuditColumns<T>(columnDefs: ColDef<T>[]): ColDef<T>[] {
  const existingFields = new Set(
    columnDefs.flatMap((col) =>
      [col.field, col.colId].filter(Boolean).map(String),
    ),
  )

  const auditCols = createAuditColumnDefs<T>().filter(
    (col) => !existingFields.has(String(col.colId)),
  )

  if (auditCols.length === 0) return columnDefs

  const actionIndex = findActionColumnIndex(columnDefs)
  if (actionIndex === -1) {
    return [...columnDefs, ...auditCols]
  }

  return [
    ...columnDefs.slice(0, actionIndex),
    ...auditCols,
    ...columnDefs.slice(actionIndex),
  ]
}

export { AUDIT_COLUMN_FIELDS }
